import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { familyPaymentsApi } from '@/lib/api';

interface UnpaidSummary {
  userId: string;
  firstName: string;
  amount: number;
}

export function PaymentAlert() {
  const queryClient = useQueryClient();
  const [payingUserId, setPayingUserId] = useState<string | null>(null);

  // Fetch all users to find family members
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get('/users');
      const data = res.data;
      return data.users || data || [];
    },
    staleTime: 1000 * 30,
  });

  const familyUsers = (users as any[]).filter((u: any) => u.role === 'family');

  // Fetch payment data for each family member
  const { data: unpaidPeople = [] } = useQuery({
    queryKey: ['payment-alert', familyUsers.map((u: any) => u.id).join(',')],
    queryFn: async () => {
      const results: UnpaidSummary[] = [];
      for (const u of familyUsers) {
        try {
          const data = await familyPaymentsApi.getPayments(u.id);
          if (data.unpaid.total_amount > 0) {
            results.push({
              userId: u.id,
              firstName: u.first_name,
              amount: data.unpaid.total_amount,
            });
          }
        } catch {
          // Skip users that fail
        }
      }
      return results;
    },
    enabled: familyUsers.length > 0,
    staleTime: 1000 * 60,
  });

  const markPaidMutation = useMutation({
    mutationFn: async (userId: string) => {
      const data = await familyPaymentsApi.getPayments(userId);
      const slotIds = data.unpaid.slots.map((s: any) => s.id);
      if (slotIds.length > 0) {
        await familyPaymentsApi.markPaid(slotIds);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-alert'] });
      queryClient.invalidateQueries({ queryKey: ['family-payments'] });
      queryClient.invalidateQueries({ queryKey: ['my-slots'] });
      queryClient.invalidateQueries({ queryKey: ['timeslots'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      setPayingUserId(null);
    },
    onError: () => {
      setPayingUserId(null);
    },
  });

  const handlePay = (userId: string) => {
    setPayingUserId(userId);
    markPaidMutation.mutate(userId);
  };

  if (unpaidPeople.length === 0) return null;

  const totalOwed = unpaidPeople.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div
      data-testid="container-payment-alert"
      className="p-3 sm:p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-400"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={18} className="flex-shrink-0" />
        <span className="font-semibold text-sm">
          ${totalOwed.toFixed(2)} owed to {unpaidPeople.length} {unpaidPeople.length === 1 ? 'person' : 'people'}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 pl-[26px]">
        {unpaidPeople.map((p) => (
          <span key={p.userId} className="inline-flex items-center gap-1.5 text-xs text-amber-400/70">
            {p.firstName}: ${p.amount.toFixed(2)}
            <button
              onClick={() => handlePay(p.userId)}
              disabled={payingUserId === p.userId}
              data-testid={`clickable-pay-${p.userId}`}
              className="px-2 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 font-medium transition-colors disabled:opacity-50"
            >
              {payingUserId === p.userId ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                'Pay'
              )}
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
